import type { AccountStatus } from "./account";
import type { HttpClient } from "./http";
import { ResourceClient } from "./resource";

/**
 * A machine caller. Authenticates with a static `dapp_*` bearer key instead of
 * a JWT, and is restricted to an explicit allowlist of endpoint codes.
 */
export interface AppAccountDTO {
  id: number;
  uuid?: string;
  username: string;
  /** SHA-256 of the key. Never useful to a client; never send it back changed. */
  secret_hash?: string;
  status: AccountStatus;
  creation_date?: string;
  method_codes: string[];
}

/**
 * One app-callable endpoint, discovered from the `@AppMethod` annotations at
 * server startup.
 *
 * Note the camelCase `httpMethod`: this is a Java record without explicit
 * `@JsonProperty` annotations, so Jackson serializes it by component name —
 * unlike the DTOs, which are snake_case throughout.
 */
export interface AppMethodDescriptor {
  code: string;
  httpMethod: string;
  path: string;
  group: string;
  summary: string;
}

/** The plaintext key is returned exactly once, at issue and at rotation. */
export interface AppAccountKeyResponse {
  account: AppAccountDTO;
  plaintext_secret: string;
}

export class AppAccountClient extends ResourceClient<AppAccountDTO> {
  constructor(http: HttpClient) {
    super(http, "/app-account");
  }

  /** Every endpoint an app account can be granted, for the allowlist picker. */
  discoverMethods(): Promise<AppMethodDescriptor[]> {
    return this.http.get<AppMethodDescriptor[]>("/app-account/methods");
  }

  /** Creates the account and issues its first key. */
  issue(account: Pick<AppAccountDTO, "username" | "status" | "method_codes">): Promise<AppAccountKeyResponse> {
    return this.http.post<AppAccountKeyResponse>("/app-account/issue", account);
  }

  /** Replaces the key. The previous one stops working immediately. */
  rotateKey(id: number): Promise<AppAccountKeyResponse> {
    return this.http.post<AppAccountKeyResponse>(`/app-account/${id}/rotate-key`);
  }
}
